import React, { useState } from 'react';
import { Menu, Typography, Tag, Alert, Divider } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

const { Title, Paragraph, Text } = Typography;

/**
 * General-purpose Help/documentation surface for InfraConsole. Designed to grow —
 * new topics are just entries in HELP_TOPICS below, each an independent React node.
 * Each topic is organized into named sections (level-4 headings, separated by
 * Dividers) rather than one long block — keep that structure when adding to a
 * topic. Keep individual topics scoped to "how do I do X in this app" — link out
 * to docs/backlog.md or docs/planning/ for architecture-level detail instead of
 * duplicating it here.
 */

interface HelpTopic {
  key: string;
  title: string;
  content: React.ReactNode;
}

const HELP_TOPICS: HelpTopic[] = [
  {
    key: 'building-blocks',
    title: 'PMA Building Blocks',
    content: (
      <>
        <Title level={3}>The Underlying Building Blocks of PMA</Title>
        <Paragraph>
          InfraConsole (also called "PMA") manages people and sessions on VLSI infrastructure,
          plus the catalog data that ties them together. Almost every other screen in the app is
          just a view onto one of the entities described below, so a few minutes here makes
          everything else easier to read. This page covers: the account-model split, the
          legacy/licensing entities, the workspace ("batch") entities, the supporting/system
          entities, and a roles reference.
        </Paragraph>

        <Divider />
        <Title level={4}>
          <Tag color="gold">Important distinction</Tag> Infra users vs. PMA application users
        </Title>
        <Paragraph>
          These are two completely separate account systems, tracked in unrelated database
          tables with no foreign key between them, and it is easy to conflate them:
        </Paragraph>
        <ul>
          <li>
            <Text strong>Infra users</Text> — people who use the infrastructure this app manages:
            legacy server accounts, workspace-container sessions, EDA tool licenses. They never
            log in to InfraConsole itself. A person could coincidentally share an email with a
            PMA application account, but nothing in the system links the two.
          </li>
          <li>
            <Text strong>PMA application users</Text> — the operators, admins, approvers,
            auditors and superadmins who log in to this console to run it. Managed on{' '}
            <Text code>User Administration</Text> (see that topic later in this list).
          </li>
        </ul>

        <Title level={5}>The two infra-user tracks</Title>
        <Paragraph>
          Within "infra users," there are also two largely separate provisioning models, tagged
          on the person's record as <Text code>provisioningMode</Text>:
        </Paragraph>
        <ul>
          <li>
            <Text code>legacy-host</Text> — the original model: a real Linux/VNC account on a
            physical or virtual server, created via the Excel/CCA bulk-generation flow. This is
            what <Text code>Server Accounts</Text> shows.
          </li>
          <li>
            <Text code>workspace-container</Text> — the newer model: a browser-based desktop
            session running as a k3s pod, orchestrated by the separate IWM system (see{' '}
            <Text code>IC and IWM: Conceptual Overview</Text>). This is what a{' '}
            <Text strong>batch</Text> / Group Instance provisions.
          </li>
        </ul>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          There is no unified "one identity across both tracks" model today beyond that tag — a
          person can, in principle, have accounts under both, tracked and managed independently.
        </Paragraph>

        <Divider />
        <Title level={4}>Infra-user and licensing entities</Title>
        <Paragraph>These back the <Text code>Accounts</Text> and <Text code>Licenses</Text> nav groups:</Paragraph>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'22%'}} /><col style={{width:'52%'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">Entity</th>
              <th className="py-1 pr-4">What it models</th>
              <th className="py-1">Where you see it</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-1 pr-4"><Text strong>Unique Infra User</Text></td>
              <td className="py-1 pr-4">One row per real person, keyed by email. Aggregates every server login account and every license that person holds, plus (for the newer model) their NFS home and workspace sessions.</td>
              <td className="py-1"><Text code>Unique Users</Text></td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1 pr-4"><Text strong>Infra User (account)</Text></td>
              <td className="py-1 pr-4">One row per legacy login on a specific server (a <Text code>UserId</Text> on a <Text code>ServerInfo</Text>), linked back to a Unique Infra User. Carries the account's expiry date and its cleanup-pipeline stage.</td>
              <td className="py-1"><Text code>Server Accounts</Text> — one person can have several of these rows.</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1 pr-4"><Text strong>Infra Server</Text></td>
              <td className="py-1 pr-4">A legacy compute server: VNC ports, capabilities (e.g. <Text code>legacy-vnc</Text>, <Text code>k3s</Text>), and an optional k3s pool label used for workspace-session placement.</td>
              <td className="py-1"><Text code>Server Info</Text></td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1 pr-4"><Text strong>License Server</Text></td>
              <td className="py-1 pr-4">A FlexLM/Synopsys license daemon host: FQDN, ports, license/opt-file paths.</td>
              <td className="py-1"><Text code>License Administration</Text> → Server Config tab</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1 pr-4"><Text strong>License (transaction)</Text></td>
              <td className="py-1 pr-4">One grant of N seats of a tool to a user on a license server, with start/planned-end dates and a status (Active / Extension Request / Expired / Disabled).</td>
              <td className="py-1"><Text code>License Administration</Text> table</td>
            </tr>
            <tr>
              <td className="py-1 pr-4"><Text strong>Extension Request</Text></td>
              <td className="py-1 pr-4">A pending change to a license's end date, tracked separately from the license so it can go through approval.</td>
              <td className="py-1"><Text code>Extension Approvals</Text></td>
            </tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>Workspace ("batch") entities</Title>
        <Paragraph>
          These live under the <Text code>Workspace</Text> nav group and only appear when the
          workspace-container feature is enabled for your deployment. They split into two layers:
          reusable catalog data (<Text strong>Definitions</Text>) and the actual running cohorts
          built from that catalog (<Text strong>Instances</Text>).
        </Paragraph>
        <Title level={5}>Definitions layer (configured once, reused by many batches)</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Entity</th>
              <th className="py-1">What it models</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Control Type</Text></td><td className="py-1">An access-control window: a period in days, a max-hours bank, and whether extensions are allowed.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>License Group Type</Text></td><td className="py-1">A named bundle of tools (e.g. "RTL2GDS" = dc, icc2, fc, prime, starrc), stored as a JSON list of tool codes.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Package</Text></td><td className="py-1">Combines a License Group Type + Control Type + license counts + a memory request/limit tier + an IWM session-profile mapping. This is the unit a batch is actually built from.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Group Type</Text></td><td className="py-1">The cohort taxonomy: <Text code>kind</Text> is one of batch / individual / corporate / trainer, and <Text code>code</Text> is a short tag — <Text code>ws</Text> (Workshop), <Text code>po</Text> (Project Oriented), <Text code>up</Text> (Upskilling), <Text code>ts</Text> (Trainer Setup), <Text code>el</Text> (E-Learning).</td></tr>
            <tr><td className="py-1 pr-4"><Text strong>NFS Location</Text></td><td className="py-1">A mountable NFS export (role: tools / labs / pdk / home), with server, source path, mount point and a read-only flag.</td></tr>
          </tbody>
        </table></div>
        <Title level={5}>Instance layer (the actual batches)</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Entity</th>
              <th className="py-1">What it models</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Group Instance</Text></td><td className="py-1">The actual batch: a named cohort with a Group Type, Package, Control Type, NFS mounts, server affinity, and a set of members. This is what most people mean by "a batch."</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Group Member</Text></td><td className="py-1">One person's membership in a Group Instance, with an <Text code>isPrimary</Text> flag used to break ties when a person belongs to more than one active group at once.</td></tr>
            <tr><td className="py-1 pr-4"><Text strong>Group Policy</Text></td><td className="py-1">The resolved, ready-to-send-to-IWM specification for a Group Instance (profile, unioned mounts, license env, TTL, node selector). Computed on demand by "Resolve Policy" and reused when a session actually starts.</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>Supporting / system entities</Title>
        <Paragraph>Less visible day-to-day, but worth knowing about:</Paragraph>
        <ul>
          <li><Text strong>System Settings</Text> — key/value configuration (feature flags like workspace-container enablement or cleanup enablement, SMTP, FreeIPA connection settings), edited on <Text code>Configuration</Text> (superadmin only).</li>
          <li><Text strong>Cleanup Alert</Text> — the audit trail for the end-of-life cleanup pipeline (one row per notification/action, sent or failed) — see <Text code>End-of-Life Cycle and Cleanup</Text>.</li>
          <li><Text strong>Transaction Log</Text> — a write-once audit record for license and account operations, viewable on <Text code>Audit Logs</Text> (auditor/superadmin).</li>
          <li><Text strong>User Preferences</Text> — per-PMA-account UI settings (theme, layout, notification toggles), private to each logged-in operator.</li>
        </ul>

        <Divider />
        <Title level={4}>Roles, at a glance</Title>
        <Paragraph>
          Enforced <Text strong>per-route on the backend</Text>, not just hidden in the sidebar —
          an operator without the right role will get a rejected request even if they can see the
          button. See the individual topics in this Help section for exactly which role a given
          action needs.
        </Paragraph>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'160px'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">Role</th>
              <th className="py-1">Typical scope</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>itoperator</Text></td><td className="py-1">Day-to-day server-account and license operations: view, disable/enable, extend expiry, restart VNC, request license extensions, reports.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>itadmin</Text></td><td className="py-1">Everything itoperator can, plus config/admin actions: bulk user generation, password reset, server config, Workspace Definitions/Groups/Sessions, cleanup pipeline oversight, extension approvals.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>superadmin</Text></td><td className="py-1">Everything — the only role for account deletion, system configuration, PMA application-user administration, license-server deletion, and Force Actions on cleanup.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>approver</Text></td><td className="py-1">License extension approvals, plus read access to licenses/users/reports. A narrow, workflow-specific role.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>auditor</Text></td><td className="py-1">Read-only: transaction/audit logs only.</td></tr>
            <tr><td className="py-1 pr-4 whitespace-nowrap"><Text code>observer</Text></td><td className="py-1">Broad read-only visibility across infra users, licenses and reports — no write actions anywhere.</td></tr>
          </tbody>
        </table></div>
      </>
    ),
  },
  {
    key: 'batch-setup',
    title: 'Setting up New Batch',
    content: (
      <>
        <Title level={3}>How to Set Up and Program a New Batch</Title>
        <Paragraph>
          A "batch" is a <Text code>Group Instance</Text> under{' '}
          <Text code>Workspace → Groups</Text> — a named cohort of people who will get
          workspace-container sessions with the same package, license pool, NFS mounts and
          placement. Setting one up is a two-layer process: first the reusable catalog data
          (Workspace Definitions), then the batch itself, its members, and an optional policy
          preview before anyone launches. This page walks through each layer in order.
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Access"
          description={
            <>
              The whole <Text code>Workspace</Text> section is hidden unless the
              workspace-container feature is enabled for your deployment. All of its APIs require{' '}
              <Text code>itadmin</Text> or <Text code>superadmin</Text> — an{' '}
              <Text code>itoperator</Text> can open these pages but every action will be rejected
              by the backend.
            </>
          }
        />

        <Divider />
        <Title level={4}>1. Prerequisite catalog data — Workspace Definitions</Title>
        <Paragraph>
          From the sidebar, go to <Text strong>Workspace → Definitions</Text>. This page has four
          tabs — <Text strong>Control Types</Text>, <Text strong>License Group Types</Text>,{' '}
          <Text strong>Packages</Text>, <Text strong>Group Types</Text> — each with its own{' '}
          <Text strong>Seed defaults</Text> button (top of the tab) that populates a starter
          catalog if the table is empty; a good way to see a working example before building your
          own.
        </Paragraph>
        <Title level={5}>Control Types tab</Title>
        <Paragraph>
          Click <Text strong>Add Control Type</Text> (top-right of the tab). In the dialog that
          opens, fill in a code and display name, the period in days, a max-hours bank, and toggle{' '}
          <Text strong>Extensions Allowed</Text> if members under this control should be able to
          request more time. Save the dialog.
        </Paragraph>
        <Title level={5}>License Group Types tab</Title>
        <Paragraph>
          Click <Text strong>Add License Group Type</Text>. Give it a code/display name and enter
          the tool codes it covers as a JSON array (e.g.{' '}
          <Text code>["dc","icc2","fc","prime","starrc"]</Text> for an "RTL2GDS" bundle). Save.
        </Paragraph>
        <Title level={5}>Packages tab</Title>
        <Paragraph>
          Click <Text strong>Add Package</Text>. In the dialog, pick the License Group Type ID and
          Control Type ID (see the callout below), set license counts, a memory request and limit
          (e.g. <Text code>4Gi</Text> / <Text code>8Gi</Text> — there's no CPU field, IWM's session
          contract doesn't have one), and pick an <Text strong>IWM Profile</Text> from the live
          dropdown sourced from IWM's own catalog (falls back to a manual text field if IWM is
          unreachable at that moment). Save. This Package is the unit a batch will actually point
          at in step 2.
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Rough edge: Packages tab fields are raw IDs"
          description="License Group Type and Control Type are selected by raw numeric ID on this dialog, not a name dropdown — open the License Group Types / Control Types tab first, note the ID of the row you want, then come back to Packages."
        />
        <Title level={5}>Group Types tab</Title>
        <Paragraph>
          Click <Text strong>Add Group Type</Text>. Enter a <Text code>code</Text> (e.g.{' '}
          <Text code>ws</Text>) and <Text code>kind</Text> (one of batch / individual / corporate /
          trainer) plus a display name — e.g. code <Text code>ws</Text>, kind{' '}
          <Text code>batch</Text>, display name "Workshop" (see{' '}
          <Text code>PMA Building Blocks</Text> for the full code list). Save.
        </Paragraph>
        <Title level={5}>NFS Locations page</Title>
        <Paragraph>
          Go to <Text strong>Workspace → NFS Locations</Text> and click{' '}
          <Text strong>Add NFS Location</Text>. Fill in a role (tools / labs / pdk / home), the NFS
          server, source path, mount path, and a read-only toggle. Save. A Group Instance
          references up to three of these (tools/labs/pdk) by role in step 2.
        </Paragraph>

        <Divider />
        <Title level={4}>2. Create the Group Instance</Title>
        <Paragraph>
          Go to <Text strong>Workspace → Groups</Text> (Group Instances). Click{' '}
          <Text strong>New Group</Text> (top-right, primary blue button). A drawer opens on the
          right titled <Text strong>New Group Instance</Text> — fill in:
        </Paragraph>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Field</th>
              <th className="py-1">What it controls</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Name / Group Type</td><td className="py-1">Identifies the batch and its cohort category (e.g. batch/ws for a workshop).</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Status</Text></td><td className="py-1">Draft (default) / Active / Closed. <Text italic>A session cannot be started for any member until this is Active</Text> — new batches start as Draft on purpose so you can finish configuring them safely.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Start / End dates</td><td className="py-1">The batch's scheduled window (informational — not itself an automatic session cutoff; see Session TTL below).</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Package / Control Type</td><td className="py-1">The Package's IWM Profile is shown read-only underneath; only override it (in the collapsed "Override IWM profile" section) if this specific batch must diverge from its Package — a legacy escape hatch.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Tools / Labs / PDK NFS</td><td className="py-1">Pick from the NFS Locations catalog, filtered by matching role.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>Affinity</Text></td><td className="py-1">Optionally pin the batch to one or more Infra Servers for session placement. Placement is only derived automatically when every server picked shares one identical k3s pool label — no multi-pool union, no hostname fallback.</td></tr>
            <tr><td className="py-1 pr-4">Scheduled Hours / Max Week Hours / Session TTL (minutes)</td><td className="py-1">Access budget plus the max session lifetime sent to IWM when a session starts — separate from any idle timeout.</td></tr>
          </tbody>
        </table></div>
        <Paragraph>
          Click <Text strong>Save</Text> at the bottom of the drawer to create the Group Instance.
          The drawer stays open (its title switches to the group's name) so you can move straight
          to adding members.
        </Paragraph>

        <Divider />
        <Title level={4}>3. Add members</Title>
        <Paragraph>
          With the group open, click <Text strong>Add Member</Text>. A dialog titled{' '}
          <Text strong>Add Member</Text> opens with three fields: <Text strong>User ID</Text>{' '}
          (required — the person's IPA/POSIX username), <Text strong>Email</Text> (optional), and
          a <Text strong>Primary</Text> switch. Fill in the User ID, toggle Primary if this is
          their only or preferred active group, and save — repeat once per participant; there's no
          bulk-add today. Mark exactly one membership <Text strong>Primary</Text> for anyone who
          belongs to more than one <Text italic>active</Text> group at once — that primary group
          is what decides their profile, placement, license and TTL when their session starts
          (mounts from every active group are unioned in). On an existing member's row, use{' '}
          <Text strong>Make Primary</Text> to flip that flag later, or <Text strong>Remove</Text>{' '}
          to take them off the batch (a confirmation dialog asks "Remove &#123;userId&#125;?" first).
        </Paragraph>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="What adding a member actually does"
          description={
            <>
              Adding a member isn't just a database row: it also provisions a FreeIPA POSIX group
              for the batch (named from the group, created on first member add) and a FreeIPA user
              for that person, then adds them to the group — the same identity workspace sessions
              and NFS home ownership rely on. This FreeIPA integration was a non-functional stub
              until recently and is still only partially confirmed against a live directory — if a
              deployment's FreeIPA isn't configured (
              <Text code>System Settings → Configuration → FreeIPA</Text>), or the integration
              silently doesn't complete, these calls can degrade to a no-op instead of failing
              loudly. Worth checking there first if a member you add doesn't show up as a real IPA
              account.
            </>
          }
        />

        <Divider />
        <Title level={4}>4. Preview with Resolve Policy</Title>
        <Paragraph>
          Back on the <Text strong>Workspace → Groups</Text> list, find your batch's row and click
          the lightning-bolt <Text strong>Resolve Policy</Text> action in that row. A read-only
          JSON preview appears showing exactly what InfraConsole will send to IWM — profile,
          unioned NFS mounts, license environment, TTL, memory, and (when unambiguous) a node
          selector — without starting anything. This is the same resolution logic actually used
          when a session starts, so it's a safe way to sanity-check a batch's configuration before
          anyone tries to launch.
        </Paragraph>

        <Divider />
        <Title level={4}>5. Launch a member's session</Title>
        <Paragraph>
          Setting up the batch doesn't launch anything by itself — see{' '}
          <Text strong>How to Monitor Running Batches</Text> for the actual Start step and the
          requirements a member must meet first (an active group, a ready NFS home, an
          unambiguous primary group).
        </Paragraph>

        <Divider />
        <Title level={4}>Known limitations</Title>
        <ul>
          <li>No bulk-add for members — added one at a time.</li>
          <li>Placement only handles the single-shared-pool-label case; no multi-pool union or hostname fallback.</li>
          <li>License Group Type / Control Type on the Packages tab are raw numeric IDs, not name pickers.</li>
          <li>The Start pipeline has been verified manually, not by automated acceptance tests.</li>
          <li>Idle-timeout policy is only applied at session creation, not re-enforced on already-running sessions.</li>
        </ul>
      </>
    ),
  },
  {
    key: 'batch-monitoring',
    title: 'Monitoring Running Batches',
    content: (
      <>
        <Title level={3}>How to Monitor Running Batches</Title>
        <Paragraph>
          <Text code>Workspace → Sessions</Text> is the operator's live view onto every
          IWM-managed workspace session — it both starts sessions and shows their current state.
          Requires <Text code>itadmin</Text> or <Text code>superadmin</Text>.
        </Paragraph>

        <Divider />
        <Title level={4}>Starting a session for a batch member</Title>
        <Paragraph>
          From the sidebar, go to <Text strong>Workspace → Sessions</Text>. Click the{' '}
          <Text strong>Start</Text> button (top-right). A dialog titled{' '}
          <Text strong>Start Workspace Session</Text> opens with one field, <Text strong>User</Text>{' '}
          — type the person's IPA/POSIX username and click <Text strong>Start</Text> to confirm.
          This is the batch-aware path — it resolves that person's entitlement rather than taking a
          raw profile id. Before it will succeed, the backend checks, in order, and the dialog
          shows exactly which check failed if one does:
        </Paragraph>
        <ol>
          <li>The <Text code>userId</Text> matches a known Unique Infra User (else <Text code>USER_NOT_FOUND</Text>).</li>
          <li>
            Their NFS home is confirmed ready (<Text code>homeReady</Text> — run{' '}
            <Text strong>Generate NFS Home</Text> on their Unique User detail page first if not;
            else <Text code>HOME_NOT_READY</Text>).
          </li>
          <li>They belong to at least one Group Instance, and at least one of those groups has <Text strong>Status = Active</Text> (else <Text code>NO_ACTIVE_GROUP</Text>, naming the non-active groups so you know exactly which one to flip).</li>
          <li>
            If they belong to more than one active group, exactly one membership is marked{' '}
            <Text strong>Primary</Text> (else <Text code>MULTIPLE_GROUPS</Text>).
          </li>
          <li>The resolved Package (or legacy override) maps to a real IWM profile (else <Text code>PROFILE_NOT_MAPPED</Text>).</li>
        </ol>
        <Paragraph>
          On success, the primary group's Package resolves to an IWM profile, NFS mounts and
          license environment are unioned across all active memberships, and — if every affinity
          server on the group shares the same k3s pool label — a node selector is added, before
          the session request goes to IWM.
        </Paragraph>
        <Title level={5}>The other way: Create Session (manual, low-level)</Title>
        <Paragraph>
          Next to Start is a second button, <Text strong>Create Session</Text>. Its dialog asks
          for two raw fields — <Text strong>User</Text> and <Text strong>Profile ID</Text> — and
          talks to IWM directly with whatever you type, bypassing all batch/policy resolution
          above entirely. Use <Text strong>Start</Text> for anything tied to a batch — treat{' '}
          <Text strong>Create Session</Text> as an admin escape hatch for one-off/manual cases,
          not the everyday path. Both buttons are disabled if IWM itself is unreachable (see
          below).
        </Paragraph>

        <Divider />
        <Title level={4}>Reading the session table</Title>
        <Paragraph>Each row shows session ID, user, resolved profile, status and creation time:</Paragraph>
        <ul>
          <li><Tag color="processing">requested / provisioning / planned</Tag> — on its way up.</li>
          <li><Tag color="success">running</Tag> — session is live.</li>
          <li><Tag color="error">failed / error</Tag> — did not come up; check the Start preconditions above.</li>
          <li><Tag color="warning">expired</Tag> — past its TTL.</li>
          <li><Tag>deleted / deleting</Tag> — torn down or tearing down; the Delete action is disabled once already reached.</li>
        </ul>

        <Divider />
        <Title level={4}>Opening a running session's desktop</Title>
        <Paragraph>
          A running session shows an <Text strong>Open</Text> link in the noVNC column — this
          opens that person's desktop in a new tab via InfraConsole's own in-cluster proxy
          (never IWM's raw, browser-unopenable <Text code>port-forward://</Text> link), restricted
          to <Text code>itadmin</Text>/<Text code>superadmin</Text>. See{' '}
          <Text code>Connecting to a VNC Session</Text> for the fuller comparison, and its caution
          that this operator-Open path is new and not yet confirmed working end-to-end in
          production.
        </Paragraph>

        <Divider />
        <Title level={4}>Stopping a session</Title>
        <Paragraph>
          <Text strong>Delete</Text> stops and tears down a session's pod. It does{' '}
          <Text italic>not</Text> remove the person from the Group Instance — they can be started
          again later as long as their group is still Active. There's no separate "stop but
          keep" state, and no automatic expiry cleans up a finished batch's sessions for you —
          see <Text code>End-of-Life Cycle and Cleanup</Text> for why that's a manual step today.
        </Paragraph>

        <Divider />
        <Title level={4}>If IWM itself is unreachable</Title>
        <Paragraph>
          The whole table is replaced by a warning banner with a <Text strong>Retry</Text> button
          instead of an empty table, and Start/Create Session are disabled until it recovers —
          that's a configuration/availability problem with the IWM deployment, not a sign the
          batch itself is broken.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'workshop-sessions',
    title: 'Launching Workshop Sessions',
    content: (
      <>
        <Title level={3}>How to Launch Workshop Sessions</Title>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Not a separate mechanism"
          description={
            <>
              "Workshop" isn't a distinct feature in InfraConsole — it's one of the standard{' '}
              <Text code>Group Type</Text> labels (<Text code>code: ws</Text>, kind{' '}
              <Text code>batch</Text>, seeded display name "Workshop"). A workshop cohort is
              simply a Group Instance built on that Group Type, so launching one follows exactly
              the batch workflow described in <Text code>Setting Up a New Batch</Text> and{' '}
              <Text code>Monitoring Running Batches</Text>.
            </>
          }
        />

        <Divider />
        <Title level={4}>Step-by-step</Title>
        <ol>
          <li>Confirm/seed a Package suited to the workshop (license group, control type, memory, IWM profile) on <Text code>Workspace → Definitions</Text>.</li>
          <li>Create a Group Instance with Group Type <Text code>batch/ws — Workshop</Text>, attach the Package, NFS mounts and (optionally) server affinity, and set a start/end window.</li>
          <li>Add every attendee as a member; mark a primary group for anyone double-booked into more than one active cohort.</li>
          <li>Switch the group's Status to <Text strong>Active</Text> once it's ready to run — nothing can start before this.</li>
          <li>On <Text code>Workspace → Sessions</Text>, use <Text strong>Start</Text> per attendee (or script it externally against <Text code>POST /api/workspace/sessions/start</Text> — the same entitlement checks apply either way).</li>
        </ol>

        <Divider />
        <Title level={4}>Who actually reaches the desktop</Title>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Participants can't self-serve a batch-aware session yet"
          description={
            <>
              Today, workshop attendees log in to their own desktop through IWM's separate{' '}
              <Text code>myws.trg</Text> participant portal, not through InfraConsole — and that
              portal starts sessions against an IWM-side default profile/entitlement, not this
              app's Group Instance/Package resolution. Bringing participant-facing "Start" into
              InfraConsole (so a workshop's Package/license/NFS policy actually applies to a
              self-service login) is planned but not live. Until then, treat{' '}
              <Text code>Workspace → Sessions → Start</Text> as the reliable, policy-correct way
              to launch a workshop attendee's session — an operator does it on the attendee's
              behalf.
            </>
          }
        />

        <Divider />
        <Title level={4}>Legacy (non-container) workshop batches — don't confuse these</Title>
        <Paragraph>
          Separately, the older Excel/CCA-based bulk user-generation flow (<Text code>User
          Generation</Text> page) still uses its own, unrelated "linux-group" code{' '}
          <Text code>ws</Text> for workshop-type legacy server accounts. That flow only creates
          plain server logins, not workspace-container sessions, and is not bridged to the Group
          Instance model above — the two share the "ws" label by convention only. Two more
          standalone tools live outside this GUI entirely for that legacy path:
        </Paragraph>
        <ul>
          <li><Text code>scripts/reset_ws_users.sh</Text> — a manual, host-run bulk password/home reset for legacy workshop accounts between classes. Defaults to a dry run; requires an explicit flag to actually act.</li>
          <li>A separate manual mail-merge script for bulk-emailing attendees their credentials.</li>
        </ul>
      </>
    ),
  },
  {
    key: 'infra-users',
    title: 'Checking Existing Infra Users',
    content: (
      <>
        <Title level={3}>How to Check Existing Infra Users</Title>
        <Paragraph>
          There are two related but different screens, both under the{' '}
          <Text code>Accounts</Text> nav group — pick based on whether you're looking at{' '}
          <Text italic>an account</Text> or <Text italic>a person</Text>.
        </Paragraph>

        <Divider />
        <Title level={4}>
          <Tag color="green">Server Accounts</Tag> one row per login, per server
        </Title>
        <Paragraph>
          Go to <Text strong>Accounts → Server Accounts</Text>. This lists individual legacy
          login accounts — one row per <Text code>UserId</Text> on a given server.
        </Paragraph>
        <Title level={5}>What you can do here</Title>
        <ul>
          <li>Filter by status (All / Active / Expired / Expiring) and pick which historical data-refresh snapshot to view, using the filter controls above the table.</li>
          <li>Search/sort columns, view SSH/VNC login commands, and use <Text strong>Export to Excel</Text> to download the current view.</li>
          <li>Use <Text strong>Configure Columns</Text> to change which columns are shown.</li>
          <li>
            Tick the checkbox on one or more rows, then open the <Text strong>Actions</Text>{' '}
            dropdown (role-permitting) for <Text strong>Disable Login</Text>,{' '}
            <Text strong>Enable Login</Text>, <Text strong>Restart VNC</Text>, and{' '}
            <Text strong>Extend Account Expiry</Text> (see{' '}
            <Text code>Workflow for User Extensions</Text> for that one's exact steps).
          </li>
          <li>For a login row missing an email, use the row's Associate Email action to attach one.</li>
        </ul>

        <Divider />
        <Title level={4}>
          <Tag color="blue">Unique Users</Tag> one row per person
        </Title>
        <Paragraph>
          Go to <Text strong>Accounts → Unique Users</Text>. This aggregates by email —
          one row per real person, across however many server accounts and licenses they hold.
          Filter by search text, license count, or active/inactive. Select rows for a bulk action
          bar: <Text strong>Add Licenses</Text>, <Text strong>View Licenses</Text>, or a bulk{' '}
          <Text strong>Generate NFS Home</Text> (paced sequentially with a short delay between
          calls to avoid hammering FreeIPA, skips rows that already have a home, and shows a
          per-row result dialog rather than one blended pass/fail toast — because a batch of users
          realistically has mixed outcomes).
        </Paragraph>

        <Title level={5}>The person detail page</Title>
        <Paragraph>Click into a person's detail page (<Text code>/user/:email</Text>) to see:</Paragraph>
        <ul>
          <li>Identity and per-server account cards, plus their license count and overall status.</li>
          <li>
            A <Text strong>Workspace Home</Text> card — whether their NFS home is provisioned,
            with a <Text strong>Generate NFS Home</Text> / <Text strong>Re-generate NFS Home</Text>{' '}
            action that opens a real SSH connection and creates the directory.
          </li>
          <li>
            A <Text strong>My Workspaces</Text> card unifying every way that person can connect —
            a Legacy VNC "Open" link plus one entry per workspace session they have (with its own
            "Open" link once running) — gated to <Text code>itadmin</Text>/<Text code>superadmin</Text>.
          </li>
          <li>Their full license history table, with the same enable/disable/extend/remove actions available from License Administration, plus (superadmin) a "Sync to Server" panel deep-linking into License Administration.</li>
        </ul>

        <Divider />
        <Title level={4}>Opening a legacy VNC session (IC-041)</Title>
        <Paragraph>
          For a person with an existing account on one of the legacy servers, with a VNC port
          already assigned:
        </Paragraph>
        <ol>
          <li>On their detail page, scroll to the <Text strong>Legacy VNC Session</Text> card and click <Text strong>Open VNC Session</Text>.</li>
          <li>A new tab opens and connects automatically. If they have more than one VNC-enabled server account, you'll be asked which server to connect to first.</li>
        </ol>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="An SSH-tunnel relay, checked live"
          description={
            <>
              InfraConsole's own backend opens an SSH tunnel to the target server (the VNC port is
              only reachable from that server's own loopback) and relays the session to your
              browser over WebSocket. If the target server is unreachable, you'll see a clear
              connection error rather than a blank screen — it's checked live at the moment you
              click Open. Access is limited to itoperator/itadmin/superadmin. For the equivalent
              flow on a workspace-container session, see <Text code>Monitoring Running Batches</Text>{' '}
              — it uses a different relay (InfraConsole's in-cluster proxy, or IWM's own participant
              portal), covered there.
            </>
          }
        />

        <Divider />
        <Title level={4}>Who can see what</Title>
        <Paragraph>
          Both pages are visible to <Text code>itoperator</Text>, <Text code>itadmin</Text>,{' '}
          <Text code>observer</Text> and <Text code>superadmin</Text>. Server Accounts is
          additionally visible to <Text code>approver</Text>; Unique Users is not — the{' '}
          <Text code>approver</Text> role can technically open the Unique Users page (no frontend
          gate blocks it) but its underlying list/detail API calls will be rejected, since that
          role isn't authorized for them server-side.
        </Paragraph>

        <Divider />
        <Title level={4}>Things to know before trusting what you see</Title>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="NFS home readiness isn't a full guarantee"
          description={
            <>
              "Generate NFS Home" marks a person <Text code>homeReady</Text>, which is what gates
              starting a workspace-container session — but this field has previously drifted out
              of sync with what IWM actually checks (see the NFS-home note under{' '}
              <Text code>Current Deployment Topology</Text> for the history). If a session Start
              fails with a home/mount error despite <Text code>homeReady</Text> being set, that's
              the first place to look.
            </>
          }
        />
      </>
    ),
  },
  {
    key: 'licensing',
    title: 'License Control Facility',
    content: (
      <>
        <Title level={3}>What Is the License Control Facility, and How to Use It</Title>
        <Paragraph>
          The Licensing control facility manages EDA tool license grants (Synopsys/FlexLM-style)
          against license servers, and the approval workflow for extending them. It spans two
          pages under the <Text code>Licenses</Text> nav group: License Administration and
          Extension Approvals.
        </Paragraph>

        <Divider />
        <Title level={4}>License Administration (<Text code>/licmgmt</Text>)</Title>
        <Title level={5}>Records tab</Title>
        <Paragraph>
          Go to <Text strong>Licenses → License Administration</Text>; the{' '}
          <Text strong>Records</Text> tab is selected by default. The main table lists license
          grants: tool, user, server, seat count, start/end date, and status (Active / Extension
          Request / Expired / Disabled). Tick the checkbox on one or more rows, then use the
          toolbar buttons — <Text strong>Disable</Text>, <Text strong>Enable</Text>,{' '}
          <Text strong>Remove</Text> (itadmin/superadmin) — or click a license's ID to open its
          full state-history timeline. A <Text strong>Sync Options File</Text> /{' '}
          <Text strong>Reread Server</Text> pair of buttons operate on a selected license server
          directly and are tracked as long-running operations rather than fire-and-forget.
        </Paragraph>
        <Title level={5}>Requesting a license extension (from this tab)</Title>
        <ol>
          <li>Select the license row(s) needing more time and click <Text strong>Request Extension</Text> in the toolbar.</li>
          <li>A dialog titled "Request License Extension" opens with two fields: <Text strong>Extension Date *</Text> (a calendar picker, required — the new date licenses should run until) and <Text strong>Remarks (Optional)</Text> (a text area for justification).</li>
          <li>Pick a date, optionally add remarks, and click <Text strong>Request Extension</Text> in the dialog footer to submit — this creates a pending Extension Request; it does not extend the license immediately (see Extension Approvals below).</li>
        </ol>
        <Title level={5}>Server Config tab</Title>
        <Paragraph>
          CRUD for the license servers themselves — FQDN, SSH access, FlexLM lmgrd/snpslmd ports,
          and the license/opt file paths used by the other two tabs. Add/edit is
          itadmin/superadmin; delete is <Text strong>superadmin only</Text> and is a soft delete —
          historic license records referencing a removed server are preserved.
        </Paragraph>
        <Title level={5}>License Update tab</Title>
        <Paragraph>
          A guided flow for rolling out a new vendor license file: pick the target server, upload
          the <Text code>.lic</Text>/<Text code>.txt</Text> file, <Text strong>Process</Text> it
          (the backend rewrites the <Text code>SERVER</Text>/<Text code>VENDOR</Text> lines to the
          server's actual hostname over SSH and shows a diff), review the patched lines, then{' '}
          <Text strong>Apply</Text>.
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Applying affects every active tool session on that server"
          description={
            <>
              Applying overwrites the live license file on the target server and triggers{' '}
              <Text code>lmreread</Text> — double-check the diff/preview before confirming, since
              this isn't scoped to one user or one tool.
            </>
          }
        />

        <Divider />
        <Title level={4}>Extension Approvals (<Text code>/extension-approvals</Text>)</Title>
        <Paragraph>
          A license extension is a two-step workflow, not an immediate change: a request gets
          submitted (above), then reviewed here by someone with approval authority.
        </Paragraph>
        <Title level={5}>Reviewing a single request</Title>
        <ol>
          <li>Go to <Text strong>Licenses → Extension Approvals</Text>. The <Text strong>Pending</Text> tab (a status filter defaulting to Pending) is the queue to work from.</li>
          <li>On the row for the request you're reviewing, click <Text strong>Approve</Text> or <Text strong>Reject</Text>.</li>
          <li>Approve opens a confirmation dialog ("Are you sure you want to approve the selected extension request(s)?") — click <Text strong>Approve</Text> again to confirm.</li>
          <li>Reject opens a dialog with a required <Text strong>Rejection Reason</Text> text box — type a reason, then click <Text strong>Reject</Text> to confirm. A reason is mandatory; the dialog won't submit without one.</li>
        </ol>
        <Title level={5}>Reviewing in bulk</Title>
        <ol>
          <li>Tick the checkbox on multiple rows in the Pending tab.</li>
          <li>A bulk action becomes available — click it to open a dialog titled "Bulk Approval" or "Bulk Rejection" depending on which you chose.</li>
          <li>Confirm with <Text strong>Approve All</Text> or <Text strong>Reject All</Text> in the dialog footer (rejection still requires a reason, applied to every selected request).</li>
        </ol>
        <Paragraph>
          Switch the status filter to Approved / Rejected, or use the All Requests view, to see
          history — per-license history is also available from the license's own record on the
          Records tab.
        </Paragraph>

        <Divider />
        <Title level={4}>Who can do what</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Action</th>
              <th className="py-1">Roles</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">View licenses / tools / servers</td><td className="py-1">itoperator, itadmin, observer, superadmin</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Enable / disable / add license, sync, reread</td><td className="py-1">itoperator, itadmin, superadmin</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Delete a license entry</td><td className="py-1">itadmin, superadmin</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">License server add/edit</td><td className="py-1">itadmin, superadmin</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">License server delete</td><td className="py-1">superadmin only</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Submit an extension request</td><td className="py-1">itoperator, itadmin, superadmin</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Approve / reject an extension request</td><td className="py-1">itadmin, approver, superadmin</td></tr>
            <tr><td className="py-1 pr-4">Transaction/audit logs</td><td className="py-1">auditor, superadmin</td></tr>
          </tbody>
        </table></div>

        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Every license action here — enable/disable/remove/extend, server sync/reread, file
          apply — also writes to the transaction log, visible in full on the{' '}
          <Text code>Audit Logs</Text> page (auditor/superadmin) — that's the place to look for a
          complete cross-license history, not just one license's own history view.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'user-extensions',
    title: 'Workflow for User Extension',
    content: (
      <>
        <Title level={3}>Workflow for User Extension</Title>
        <Paragraph>
          "Extension" means two different things in this app depending on which screen you're
          on — worth keeping straight, since they have different approval models and live on
          different pages.
        </Paragraph>

        <Divider />
        <Title level={4}>
          <Tag color="blue">Account expiry extension</Tag> — how long a login is valid
        </Title>
        <Paragraph>
          This is about extending a <Text italic>login account's</Text> expiry date. It has no
          approval step — it's a direct operator/admin action.
        </Paragraph>
        <ol>
          <li>Go to <Text strong>Accounts → Server Accounts</Text>. Tick the checkbox on one or more rows for the accounts whose expiry needs pushing out — this is required first; the extend control stays disabled with nothing selected.</li>
          <li>Click the <Text strong>Actions</Text> dropdown (shows a badge with your selected-row count) and find the <Text strong>Extend Account Expiry</Text> section inside it.</li>
          <li>Pick a future date in the date field there, then click <Text strong>Extend Selected Users</Text> (disabled until you've selected rows and chosen a date later than today).</li>
          <li>For each selected account, InfraConsole connects over SSH to that account's server and extends the OS-level account expiry directly, then updates its own record to match.</li>
        </ol>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="This also clears any in-progress cleanup — but only up to a point"
          description={
            <>
              If the account had already entered the end-of-life cleanup pipeline (grace period
              or an approaching-cleanup alert stage — see{' '}
              <Text code>End-of-Life Cycle and Cleanup</Text>), a successful extension resets that
              pipeline back to a clean state, so the nightly cleanup job doesn't act on a now-stale
              expiry date. This reset only applies to the early alert stages — once a home
              directory has actually been archived, extending the expiry does{' '}
              <Text strong>not</Text> undo that; recovering it is a manual step for whoever
              manages the cleanup job.
            </>
          }
        />
        <Paragraph>
          Requires <Text code>itoperator</Text>, <Text code>itadmin</Text> or{' '}
          <Text code>superadmin</Text>. Special/service accounts are excluded and will report
          "can't operate on special accounts" if selected by mistake.
        </Paragraph>

        <Divider />
        <Title level={4}>
          <Tag color="purple">License extension request</Tag> — how long a tool license lasts
        </Title>
        <Paragraph>
          This one <Text strong>is</Text> a formal request/approval workflow, entirely separate
          from account expiry:
        </Paragraph>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'22%'}} /><col style={{width:'28%'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">Step</th>
              <th className="py-1 pr-4">Who</th>
              <th className="py-1">Where</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Submit request</td><td className="py-1 pr-4">itoperator, itadmin, superadmin</td><td className="py-1">License Administration → Records → Request Extension</td></tr>
            <tr><td className="py-1 pr-4">Approve / reject</td><td className="py-1 pr-4">itadmin, approver, superadmin</td><td className="py-1">Extension Approvals</td></tr>
          </tbody>
        </table></div>
        <Paragraph>
          See <Text strong>Licensing Control Facility</Text> for the full walkthrough of that
          workflow — it isn't duplicated here.
        </Paragraph>

        <Divider />
        <Title level={4}>Rule of thumb</Title>
        <Alert
          type="info"
          showIcon
          message="Which one do I need?"
          description={
            <>
              If you're extending <Text italic>how long someone can log in</Text>, that's Server
              Accounts → Extend Expiry (instant, no approval). If you're extending{' '}
              <Text italic>how long someone keeps a tool license</Text>, that's a License Extension
              Request (goes through approval).
            </>
          }
        />
      </>
    ),
  },
  {
    key: 'lifecycle-cleanup',
    title: 'End-of-Life Cycle Control and Cleanup',
    content: (
      <>
        <Title level={3}>How End-of-Life Cycle Control and Cleanup Works</Title>
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Not yet soaked in production — read this whole page first"
          description={
            <>
              This pipeline is implemented and scheduled in this codebase, but the backlog
              explicitly records that a <Text strong>staging soak before turning it on in
              production has not happened yet</Text>, and lists several concrete gaps (below).
              Treat everything on this page as "implemented and observable, but not yet proven
              safe at scale" — not a mature, battle-tested workflow.
            </>
          }
        />

        <Divider />
        <Title level={4}>Where it lives</Title>
        <Paragraph>
          Go to <Text strong>Operations → Cleanup Tracking</Text> (<Text code>itadmin</Text>/
          <Text code>superadmin</Text>). It has four tabs:{' '}
          <Text strong>In Pipeline</Text> (accounts currently mid-lifecycle),{' '}
          <Text strong>Completed</Text> (accounts that finished cleanup),{' '}
          <Text strong>Alert Log</Text> (every notification/attempt, sent or failed), and{' '}
          <Text strong>Force Actions</Text> (superadmin-only manual overrides).
        </Paragraph>

        <Divider />
        <Title level={4}>The automated pipeline</Title>
        <Paragraph>
          A nightly job (2 AM) scans <Text italic>legacy server accounts</Text> whose{' '}
          <Text code>AccountExpiry</Text> has passed and walks each one through fixed stages:
        </Paragraph>
        <Paragraph>
          <Text code>null → GRACE → ALERT_30D → ALERT_7D → ALERT_1D → HOME_DELETED → COMPLETED</Text>
        </Paragraph>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'22%'}} /><col style={{width:'28%'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">Stage</th>
              <th className="py-1 pr-4">Trigger</th>
              <th className="py-1">What happens / reversible?</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">GRACE</td><td className="py-1 pr-4">Expiry date passes</td><td className="py-1">Scheduled cleanup date set (expiry + grace period, default 60 days); initial email sent. Reversible — cleared by extending expiry.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">ALERT_30D / 7D / 1D</td><td className="py-1 pr-4">Scheduled date approaching</td><td className="py-1">Reminder email (each stage gated so it won't re-send within 20 hours of the last one). Reversible.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4"><Text strong>HOME_DELETED</Text></td><td className="py-1 pr-4">Scheduled date passes</td><td className="py-1"><Text strong>Irreversible.</Text> SSH connects and archives (moves) the home directory to a backup path on the same server; records the backup location; emails the user.</td></tr>
            <tr><td className="py-1 pr-4"><Text strong>COMPLETED</Text></td><td className="py-1 pr-4">Backup retention elapses (default 7 days)</td><td className="py-1"><Text strong>Irreversible.</Text> SSH deletes the archived backup; record marked complete.</td></tr>
          </tbody>
        </table></div>
        <Paragraph>
          Two built-in safety checks run before any destructive step: accounts with sudo/admin
          privileges on their server are always skipped, and if an account's expiry is pushed into
          the future again (e.g. via <Text code>Workflow for User Extensions</Text>) before its
          home directory has been archived, the pipeline auto-resets to a clean state on the next
          run — but this self-heal only covers the pre-archival stages, never after.
        </Paragraph>

        <Divider />
        <Title level={4}>The master switch is deliberately hard to reach</Title>
        <Paragraph>
          The whole job no-ops unless <Text code>CLEANUP_ENABLED</Text> is <Text code>true</Text>.
          That setting is visible on <Text code>System Settings</Text>, but its toggle is locked in
          the GUI on purpose — enabling it requires a direct database update, not a click. That's
          intentional: it keeps a config typo from accidentally starting automatic home deletion.
          The grace period, backup retention period, and backup base path are configurable from{' '}
          <Text code>System Settings</Text> once cleanup is enabled.
        </Paragraph>

        <Divider />
        <Title level={4}>Force Actions (manual override, superadmin)</Title>
        <ol>
          <li>Go to <Text strong>Operations → Cleanup Tracking</Text> and select the <Text strong>Force Actions</Text> tab (marked with a warning icon — only visible to superadmin).</li>
          <li>Pick the account from the pipeline list shown on that tab.</li>
          <li>Choose an action: <Text strong>Force Archive Home</Text> (immediately move the home directory to backup via SSH), <Text strong>Force Purge Backup</Text> (permanently delete the archived backup — cannot be undone), or <Text strong>Cancel Cleanup</Text> (remove the user from the pipeline and reset all cleanup fields).</li>
          <li>Type the account's exact user ID into the confirmation field — the submit control stays locked until what you type matches exactly.</li>
          <li>Type a reason of more than 5 characters into the Reason field (required for every action, including Cancel).</li>
          <li>Click the submit button to execute immediately over SSH — this bypasses the nightly schedule entirely and is logged the same way as the automated stages.</li>
        </ol>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Force Archive Home and Force Purge Backup are exactly as destructive as their automated
          equivalents (HOME_DELETED / COMPLETED) — treat the confirmation friction here as
          intentional, not an inconvenience to work around quickly.
        </Paragraph>

        <Divider />
        <Title level={4}>Gaps to know about before relying on this</Title>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Concrete, current caveats"
          description={
            <ul className="mb-0 pl-4">
              <li>The documented exclusion list (service-account prefixes, shared homes, certain groups) is not implemented — the only real exemption check today is whether the account has admin/sudo access.</li>
              <li>There are no automated tests for the cleanup job or its controller.</li>
              <li>The SSH archive/purge commands are built from string interpolation rather than parameterized — a real defect to fix before wider rollout, not a documentation nitpick.</li>
              <li>There is no dry-run mode in the job itself.</li>
            </ul>
          }
        />
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Does not (yet) cover workspace-container / NFS-home users"
          description={
            <>
              This pipeline only understands the legacy per-server-account model. The newer
              NFS-home path used by workspace/batch participants has its own provisioner with
              working archive/delete functions, but as of this writing nothing calls them — there
              is no cleanup wiring for NFS-homed users at all yet. If a workshop or batch
              participant's access needs to be torn down, that home directory reclaim is a manual
              job today, not something this page or the nightly cron will do for you.
            </>
          }
        />
      </>
    ),
  },
  {
    key: 'ic-iwm-overview',
    title: 'IC & IWM',
    content: (
      <>
        <Title level={3}>A Conceptual Overview of IC and IWM, and How They Interface</Title>
        <Paragraph>
          <Text strong>IC</Text> (InfraConsole — this application, also called "PMA") and{' '}
          <Text strong>IWM</Text> (Infra Workspace Manager) are two separate codebases with a
          deliberate split of responsibility for workspace-container sessions.
        </Paragraph>

        <Divider />
        <Title level={4}>Division of responsibility</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'18%'}} /><col style={{width:'41%'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">&nbsp;</th>
              <th className="py-1 pr-4">IC (this app)</th>
              <th className="py-1">IWM</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Role</td><td className="py-1 pr-4">Entitlement resolver + operator UI</td><td className="py-1">Executor</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Owns</td><td className="py-1 pr-4">Group Instance / Package / member / license / infra-user data model; decides who is allowed to run what, with which mounts and license environment</td><td className="py-1">Session profile catalog; actual Kubernetes pod lifecycle (create/list/delete); home-mountability check; running-session TTL/idle enforcement</td></tr>
            <tr><td className="py-1 pr-4">Never does</td><td className="py-1 pr-4">Talk to Kubernetes directly for sessions — always goes through IWM's API</td><td className="py-1">Resolve batch/entitlement policy — that's IC's job, IWM just executes what it's asked</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>How a session gets created</Title>
        <Paragraph>
          When an operator clicks <Text strong>Start</Text> for a batch member (see{' '}
          <Text code>Monitoring Running Batches</Text>), IC resolves that person's active Group
          Instance into a concrete request — IWM profile id, NFS mounts, license environment
          variables, idle timeout, TTL, memory, and (when unambiguous) a node selector — and calls
          IWM's session-create API with it. IWM is the one that actually schedules the pod and
          reports back status (requested → provisioning → running, or failed/expired).
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="IC talks to IWM using an admin token today, not the intended portal token"
          description={
            <>
              IWM defines three token roles (admin / portal / user). IC's session-start call is
              meant to use the narrower <Text code>portal</Text> token, but the real portal secret
              hasn't been copied into this app's environment yet — so it currently falls back to
              the broader admin token, with a warning logged. Sessions still start; it's a
              least-privilege gap to be aware of, not a broken feature.
            </>
          }
        />

        <Divider />
        <Title level={4}>Why the desktop can't just open directly</Title>
        <Paragraph>
          IWM returns a <Text code>novncUrl</Text> shaped like{' '}
          <Text code>port-forward://{'{namespace}'}/{'{service}'}</Text> — not a URL a browser can
          open — and by design the session's noVNC service is cluster-internal only (it runs
          without its own password, so exposing it directly would be a real security hole). Every
          desktop connection in this app is therefore relayed through a fixed host: legacy VNC
          through InfraConsole's own backend over SSH; workspace sessions through IWM's
          participant portal (for participants) or InfraConsole's in-cluster proxy (for
          operators) — see <Text code>Connecting to a VNC Session</Text>.
        </Paragraph>

        <Divider />
        <Title level={4}>How IC and IPA (FreeIPA) fit in</Title>
        <Paragraph>
          FreeIPA is the shared identity source both systems rely on for POSIX users/groups.
          Concretely: when a member is added to a batch in IC, IC itself calls FreeIPA to ensure a
          POSIX group exists for that batch and that the member's IPA user exists and belongs to
          it — a real, direct IC-to-IPA integration, not something IWM does on IC's behalf. IWM
          separately depends on the same FreeIPA realm for its own session identity and for the
          participant portal's login.
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="This integration is recent and only partially confirmed"
          description="It was a non-functional stub until very recently, and hasn't yet been proven against a live directory via its own built-in connection test — though indirect evidence (a couple of real, working provisioned homes) suggests it has succeeded before. If a batch member can't start a session, an IPA-provisioning failure on Add Member is a plausible cause to check."
        />

        <Divider />
        <Title level={4}>What's still on the IWM side, not IC's</Title>
        <Paragraph>
          Participant-facing desktop access (a workshop attendee logging in and clicking Start
          themselves) still runs through IWM's own <Text code>myws.trg</Text> portal today, using
          IWM's own default-profile logic rather than IC's Group-Instance/Package resolution.
          Bringing that participant flow into IC — so a self-service login actually respects a
          batch's configured policy — is planned (see{' '}
          <Text code>docs/planning/myws-to-ic-cutover.md</Text> in the repository) but not live.
          Operators should keep using IC's <Text code>Start</Text> button on a participant's
          behalf until that cutover lands.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          The legacy CCA/SMS user-generation tooling (bulk Excel-based server-account creation) is
          a third, unrelated system IC also drives, for the older per-server-account model — it
          predates and does not talk to IWM at all; the two happening to run on the same physical
          server is a deployment coincidence, not an integration.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'deployment',
    title: 'Current Deployment Topology',
    content: (
      <>
        <Title level={3}>How the Current Deployment Is Set Up</Title>
        <Paragraph>
          This describes the current lab (TRG) k3s deployment as recorded in the repository's ops
          docs — treat exact hostnames/ports as a snapshot that can drift; the shape of the
          relationships is the durable part.
        </Paragraph>

        <Divider />
        <Title level={4}>Namespace / pod inventory</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'18%'}} /><col style={{width:'32%'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">Namespace</th>
              <th className="py-1 pr-4">Workload</th>
              <th className="py-1">Role</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">infraconsole</td><td className="py-1 pr-4">pma-backend, pma-frontend, self-service-*</td><td className="py-1">This app — operator GUI/API and the standalone password self-service app. Pinned to the control-plane node, since the worker node is frequently unavailable (see Known limitations below).</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">iwm-system</td><td className="py-1 pr-4">workspace-manager (API), workspace-manager-ui</td><td className="py-1">IWM core — owned by the IWM team; InfraConsole only calls its API, never modifies it directly.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">iwm-myws</td><td className="py-1 pr-4">myws-portal</td><td className="py-1">IWM's temporary participant desktop portal.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">platform-registry</td><td className="py-1 pr-4">registry</td><td className="py-1">Site Docker image registry this app's images are pushed to and pulled from.</td></tr>
            <tr><td className="py-1 pr-4">kube-system</td><td className="py-1 pr-4">traefik, coredns</td><td className="py-1">In-cluster routing; TLS terminates upstream of the cluster, outside this app's control.</td></tr>
          </tbody>
        </table></div>

        <Title level={5}>Ingress map</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse table-fixed">
          <colgroup><col style={{width:'240px'}} /><col /></colgroup>
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4">Hostname / path</th>
              <th className="py-1">Routes to</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>pmav2.trg.vlsiexpert.in</Text></td><td className="py-1">pma-frontend — this app's operator UI.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>myws.trg.vlsiexpert.in/</Text></td><td className="py-1">myws-portal — IWM's participant desktop portal.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>myws.trg.vlsiexpert.in/reset*</Text></td><td className="py-1">This app's password self-service flow (shares the host; longer path prefix wins).</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4 whitespace-nowrap"><Text code>iwm.trg.vlsiexpert.in</Text></td><td className="py-1">IWM's own operator/catalog UI.</td></tr>
            <tr><td className="py-1 pr-4 whitespace-nowrap"><Text code>ipa.trg.vlsiexpert.in</Text></td><td className="py-1">FreeIPA (documentation entry only — not changed by this app).</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>IPA (identity) — two distinct uses, don't conflate</Title>
        <Title level={5}>1. Static container identity</Title>
        <Paragraph>
          InfraConsole's own backend containers run as a fixed FreeIPA-registered UID/GID (a
          dedicated service account), baked into the image and deployment manifest at build/deploy
          time, so hostPath-mounted directories the backend touches have consistent, correct
          ownership. The container itself never talks live to IPA for this.
        </Paragraph>
        <Title level={5}>2. Live identity operations</Title>
        <Paragraph>
          Separately, when operators build batches in IC, the backend makes real, live calls to
          FreeIPA (configured under <Text code>System Settings → Configuration → FreeIPA</Text>,
          superadmin-only) to allocate a real UID/GID and POSIX group for each batch member before
          their NFS home or IWM session can exist — see{' '}
          <Text code>IC and IWM: Conceptual Overview</Text> for the caveat that this is recent and
          only partially confirmed against a live directory.
        </Paragraph>
        <Paragraph>
          The participant portal (IWM's <Text code>myws</Text>) authenticates people against the
          same FreeIPA realm via LDAP bind.
        </Paragraph>

        <Divider />
        <Title level={4}>NFS home</Title>
        <Paragraph>
          Two different things both involve NFS — keep them separate. <Text strong>NFS Location</Text>{' '}
          entries (<Text code>Workspace → NFS Locations</Text>) are a generic mount catalog — a
          batch references them by role (tools/labs/pdk/home) to tell IWM what to mount inside a
          session. <Text strong>Generate NFS Home</Text> (on a person's detail page) is a separate,
          direct action: it opens a real SSH connection to the configured NFS home host and
          creates the person's home directory there with the right ownership — this has to happen
          as true root on the NFS server itself, which is why it's a live SSH step rather than
          something the cluster does on its own.
        </Paragraph>
        <Paragraph>
          The current default target is{' '}
          <Text code>nfshome.trg.vlsiexpert.in:/home/nfshome/&#123;user&#125;</Text> — chosen to
          match IWM's own live mount check after an earlier mismatch (a different interim
          host/path) silently broke session starts despite accounts showing{' '}
          <Text code>homeReady</Text>.
        </Paragraph>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="This target has moved before and may move again"
          description="Don't hardcode it into other tooling — check the live NFS home provisioner configuration rather than trusting older planning docs if you need the current value."
        />

        <Divider />
        <Title level={4}>Known limitations of this deployment</Title>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Effectively a single-node cluster today"
          description="The k3s cluster has two nodes in principle, but the second has an open, unresolved cross-node networking defect that makes pods scheduled there unreachable from the control-plane node. In practice everything this app depends on runs pinned to the one reliable node."
        />

        <Divider />
        <Title level={4}>Putting it together</Title>
        <Paragraph>
          A workspace session, end to end: an operator (or eventually a participant) triggers
          Start in IC → IC resolves the batch's Package/Group Policy and confirms the person's
          FreeIPA identity and NFS home are provisioned → IC calls IWM's API with the resolved
          spec → IWM schedules the session pod in the cluster, mounting that person's NFS home and
          the batch's tools/labs/pdk NFS locations → the participant (or, for now, an operator via
          IC's proxy) reaches the running desktop over noVNC, relayed rather than exposed directly
          on the network.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          This snapshot describes the TRG lab deployment as of this writing; topology, node
          reachability, and integration status (especially FreeIPA and the operator noVNC proxy)
          are actively changing. For the most current state, check <Text code>docs/backlog.md</Text>{' '}
          and <Text code>docs/deploy/</Text> in the repository rather than relying solely on this
          page.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'bulk-excel',
    title: 'Bulk Excel',
    content: (
      <>
        <Title level={3}>Bulk Excel: Generating InfraUser Accounts from a Spreadsheet</Title>
        <Paragraph>
          The <Text strong>Bulk Excel</Text> tab on{' '}
          <Text code>Accounts → User Generation</Text> (<Text code>/gen-users</Text>) lets you
          upload an <Text code>.xlsx</Text> file and generate legacy server accounts for all rows
          in one operation. The workflow is a five-step visual stepper:{' '}
          <Text strong>Upload → Validate → Confirm → Run → Results</Text>.
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Role gap: itoperator can open the page but cannot generate"
          description={
            <>
              The <Text code>/gen-users</Text> route allows <Text code>itoperator</Text>,{' '}
              <Text code>itadmin</Text>, and <Text code>superadmin</Text> to navigate to the page.
              However, the generate endpoint (<Text code>POST /infrauser/generatestd</Text>) requires{' '}
              <Text code>itadmin</Text> or <Text code>superadmin</Text> — an{' '}
              <Text code>itoperator</Text> who clicks <Text strong>Generate Users</Text> will get a
              server-side rejection even though the button is visible.
            </>
          }
        />

        <Divider />
        <Title level={4}>Step 1 — Upload</Title>
        <Paragraph>
          The tab opens with a drop zone labelled{' '}
          <Text italic>"Drag 'n' drop an .xlsx file, or click to select one"</Text>. Two buttons
          sit below it:
        </Paragraph>
        <ul>
          <li>
            <Text strong>Download Sample Template</Text> — fetches the expected column layout as an
            Excel file so you can populate it correctly before uploading your own data.
          </li>
          <li>
            <Text strong>Reset</Text> — clears the current upload and returns the stepper to step 1.
          </li>
        </ul>

        <Divider />
        <Title level={4}>Step 2 — Validate</Title>
        <Paragraph>
          Once a file is uploaded and its rows appear, an{' '}
          <Text strong>Input Record and User Check</Text> section appears. Click{' '}
          <Text strong>Validate input records and users</Text> to run duplicate and field checks.
          The result shows a summary panel — Selected count, Duplicate count, and related figures.
          If issues are found, a <Text strong>Show Details</Text> /{' '}
          <Text strong>Hide Details</Text> toggle expands the per-row breakdown. Validation must
          pass (no blocking issues) before the stepper advances to Confirm.
        </Paragraph>

        <Divider />
        <Title level={4}>Step 3 — Confirm (optional preflight)</Title>
        <Paragraph>
          Before generating, an amber{' '}
          <Text strong>Check servers before generate</Text> button runs a preflight that warns
          about users already present on a target server, or leftover configuration files under{' '}
          <Text code>/opt/sms/cfg/users</Text> from a failed prior run. This step is optional but
          recommended when re-running after a partial failure.
        </Paragraph>

        <Divider />
        <Title level={4}>Step 4 — Run</Title>
        <Paragraph>
          Click <Text strong>Generate Users</Text> (green) to submit the file to{' '}
          <Text code>POST /infrauser/generatestd</Text> as a multipart upload. The backend starts
          the operation and returns an <Text code>operationId</Text>.
        </Paragraph>

        <Divider />
        <Title level={4}>Step 5 — Results</Title>
        <Paragraph>
          An <Text strong>OperationProgressPanel</Text> polls{' '}
          <Text code>GET /infrauser/operation-status/:operationId</Text> (default every 2 s, env
          var <Text code>REACT_APP_FRONTEND_POLLING_INTERVAL_SECONDS</Text>; hard timeout from{' '}
          <Text code>REACT_APP_HARD_TIMEOUT_SECONDS</Text>, default 660 s) and streams progress
          in real time. Results are shown per row — each input record reports success or failure
          independently rather than a single blended toast. A{' '}
          <Text strong>Download Results</Text> link becomes available via{' '}
          <Text code>GET /infrauser/operation-result/:operationId/download</Text> once the
          operation completes.
        </Paragraph>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Same progress/download mechanism on all three tabs"
          description={
            <>
              The Single User and Add N Users tabs share the same{' '}
              <Text code>operationId</Text> polling and download flow described above — it is not
              repeated in those topics, only cross-referenced.
            </>
          }
        />
      </>
    ),
  },
  {
    key: 'single-user',
    title: 'Single User',
    content: (
      <>
        <Title level={3}>Single User: Generating One InfraUser Account</Title>
        <Paragraph>
          The <Text strong>Single User</Text> tab on{' '}
          <Text code>Accounts → User Generation</Text> creates one legacy server account at a
          time via a form divided into four labelled sections. Requires <Text code>itadmin</Text>{' '}
          or <Text code>superadmin</Text> (same role gap as Bulk Excel applies — see that topic).
        </Paragraph>

        <Divider />
        <Title level={4}>Identity section</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Field</th>
              <th className="py-1">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">User Name *</td><td className="py-1">Required.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Email *</td><td className="py-1">Required; duplicate-checked live before submission.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">User ID *</td><td className="py-1">Required; duplicate-checked live before submission.</td></tr>
            <tr><td className="py-1 pr-4">Mobile</td><td className="py-1">Optional.</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>Account section</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Field</th>
              <th className="py-1">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Linux Group *</td><td className="py-1">Required; maps to <Text code>primary_group</Text>. Determines the home mount path used by the skip-mount-check option.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Secondary Groups</td><td className="py-1">Optional; space-separated, placeholder <Text code>group1 group2</Text>.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">User Type</td><td className="py-1">Optional.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Initial Password</td><td className="py-1">Show/hide toggle on the input. Defaults to a system value if left blank.</td></tr>
            <tr><td className="py-1 pr-4">Remarks</td><td className="py-1">Optional free-text note.</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>System & Access section</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Field</th>
              <th className="py-1">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">System Type *</td><td className="py-1">Required; select <Text code>vnc</Text> / <Text code>putty</Text> / <Text code>both</Text>. Controls which FQDN fields are enabled.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Tools *</td><td className="py-1">Required; placeholder <Text code>icc2=1:prime=1</Text>, format <Text code>tool=number:tool=number</Text>.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">PuTTY FQDN</td><td className="py-1">Select from Infra Servers. Enabled and required only when System Type is <Text code>putty</Text> or <Text code>both</Text>.</td></tr>
            <tr><td className="py-1 pr-4">VNC FQDN</td><td className="py-1">Select from Infra Servers. Enabled and required only when System Type is <Text code>vnc</Text> or <Text code>both</Text>.</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>Dates section</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Field</th>
              <th className="py-1">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Setup Date</td><td className="py-1">Optional; defaults to today.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Expiry Date</td><td className="py-1">Optional; defaults to three months from today.</td></tr>
            <tr><td className="py-1 pr-4">Duration</td><td className="py-1">Optional; placeholder <Text code>90d or 3m</Text>.</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>Execution Options</Title>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Advanced option — not a normal step"
          description={
            <>
              The <Text strong>Execution Options</Text> box contains a single checkbox:{' '}
              <Text strong>Skip mount-point validation (/ve/&lt;group&gt;_home)</Text>. By default
              (unchecked) the system verifies that the required mount partition exists on the target
              server before creating the account. Checking this bypasses that verification — the
              section header changes to a red <Text strong>Execution Warning</Text> styling to make
              it visible. Use only when you know the mount exists and the check is failing
              incorrectly.
            </>
          }
        />

        <Divider />
        <Title level={4}>Submitting</Title>
        <Paragraph>
          Click <Text strong>Generate User</Text>. The form first runs a live duplicate check on
          email and User ID; if a conflict is found it reports that immediately rather than
          submitting. On a clean check, the request goes to{' '}
          <Text code>POST /infrauser/generate-users</Text>. If the backend returns an{' '}
          <Text code>operationId</Text>, the progress panel polls and streams results; otherwise
          it reports immediate completion. See <Text strong>Bulk Excel</Text> for the shared
          progress/download mechanism details.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'add-n-users',
    title: 'Add N Users',
    content: (
      <>
        <Title level={3}>Add N Users: Bulk-Generating N Similar Accounts on One System</Title>
        <Paragraph>
          The <Text strong>Add N Users</Text> tab on{' '}
          <Text code>Accounts → User Generation</Text> generates a fixed number of similarly
          named legacy accounts on a single target server in one request — useful for spinning up
          a workshop cohort without an Excel file. Requires <Text code>itadmin</Text> or{' '}
          <Text code>superadmin</Text> (same role gap as Bulk Excel applies — see that topic).
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="These accounts are not tracked on the Unique Users page"
          description={
            <>
              As stated in the app itself: <Text italic>"Users generated here are not tracked
              through Unique users in this console. This functionality may be enhanced in future
              depending upon need."</Text> Accounts created via Add N Users will not appear on{' '}
              <Text code>Accounts → Unique Users</Text>.
            </>
          }
        />

        <Divider />
        <Title level={4}>Form fields</Title>
        <div className="overflow-x-auto mb-4"><table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-1 pr-4 whitespace-nowrap">Field</th>
              <th className="py-1">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Target System (VNC FQDN) *</td><td className="py-1">Required; select from Infra Servers. The <Text strong>Generate N Users</Text> button stays disabled until a system is chosen.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Number of users *</td><td className="py-1">Required; numeric, minimum 1.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Linux Group</td><td className="py-1">Select: <Text code>ws</Text> / <Text code>po</Text> / <Text code>up</Text> / <Text code>ts</Text> / <Text code>el</Text>. Choosing a group auto-fills the Prefix field (e.g. selecting <Text code>ws</Text> sets Prefix to <Text code>ws_</Text>). These codes share vocabulary with the newer workspace Group Type codes by convention — see <Text code>PMA Building Blocks</Text>.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Login type</td><td className="py-1">Select: <Text code>vnc</Text> / <Text code>putty</Text> / <Text code>both</Text>.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Prefix</td><td className="py-1">Text; auto-filled from Linux Group selection (e.g. <Text code>ws_</Text>), editable.</td></tr>
            <tr className="border-b border-gray-100"><td className="py-1 pr-4">Expiry</td><td className="py-1">Placeholder <Text code>e.g. 6m</Text>; defaults to 6 months from today if left blank.</td></tr>
            <tr><td className="py-1 pr-4">Index start number</td><td className="py-1">Numeric, minimum 0. Determines the numeric suffix of the first generated account.</td></tr>
          </tbody>
        </table></div>

        <Divider />
        <Title level={4}>Submitting</Title>
        <Paragraph>
          Click <Text strong>Generate N Users</Text> (shows <Text italic>"Generating..."</Text>{' '}
          while in flight). The request goes to <Text code>POST /infrauser/addnumusers</Text>,
          which maps to{' '}
          <Text code>cca user addnumusers TargetSystem NumUser [linuxgroup] [loginType] [prefix] [expiry] [index_start_number]</Text>{' '}
          on the backend. Progress and results use the shared{' '}
          <Text code>operationId</Text> polling and download mechanism described in{' '}
          <Text strong>Bulk Excel</Text>.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'user-admin',
    title: 'User Administration',
    content: (
      <>
        <Title level={3}>User Administration Functionality</Title>
        <Paragraph>
          <Text code>User Administration</Text> (<Text code>/user-management</Text>,{' '}
          <Text code>superadmin</Text> only) manages the login accounts for InfraConsole itself —
          the people who use this app, not the infra users it manages. Don't confuse this with{' '}
          <Text code>Server Accounts</Text> / <Text code>Unique Users</Text>, which are about the
          infrastructure's own users; see <Text code>PMA Building Blocks</Text> for that
          distinction.
        </Paragraph>

        <Divider />
        <Title level={4}>What a superadmin can do here</Title>
        <Paragraph>Go to <Text strong>System → User Administration</Text> from the sidebar.</Paragraph>
        <Title level={5}>Change a person's role</Title>
        <Paragraph>
          In the <Text strong>Role</Text> column on their row, open the dropdown and pick one of
          IT Operator, IT Admin, Observer, Auditor, Approver, or Superadmin. The change is saved
          immediately on selection — there is no separate Save/Confirm click and no confirmation
          dialog for role changes.
        </Paragraph>
        <Title level={5}>Enable / disable an account</Title>
        <Paragraph>
          In the <Text strong>Status</Text> column, flip the switch next to their row. Turning it{' '}
          <Text italic>off</Text> opens a confirmation dialog ("Disable this user? … will be
          immediately signed out and unable to log in until re-enabled.") — click{' '}
          <Text strong>Disable</Text> to confirm; the person is signed out immediately. Turning it{' '}
          <Text italic>on</Text> re-enables the account right away, no confirmation needed.
        </Paragraph>
        <Title level={5}>Reset a password</Title>
        <Paragraph>
          In the <Text strong>Actions</Text> column, click <Text strong>Reset Password</Text>{' '}
          (only enabled for local email/password accounts — greyed out with a tooltip for
          Google/GitHub OAuth accounts, which have no local password). Confirming generates a new
          random temporary password and invalidates the old one immediately; the new password is
          shown once on screen — copy it and share it with the user through a secure channel out
          of band, since it won't be shown again.
        </Paragraph>
        <Paragraph>
          The table also shows each account's login method (local / google / github), email
          verification status, and creation date. A <Text strong>Refresh</Text> control reloads
          the list.
        </Paragraph>

        <Divider />
        <Title level={4}>What you can't do here</Title>
        <Paragraph>
          There is no account creation or deletion flow on this page — new PMA application
          accounts come from registration or OAuth sign-in, not from an admin creating them
          directly.
        </Paragraph>

        <Divider />
        <Title level={4}>Roles reference</Title>
        <Paragraph>
          See the full roles table in <Text code>PMA Building Blocks</Text> for what each role can
          do across the app; this page is only where the role assignment itself happens.
        </Paragraph>

        <Divider />
        <Title level={4}>Built-in safety rails — and one gap to be deliberate about</Title>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Self-protection is enforced; superadmin promotion is not extra-gated"
          description={
            <>
              A superadmin cannot change their own role or disable their own account from this
              screen — both controls are disabled on your own row, so you can't accidentally lock
              yourself out. There is, however, no extra confirmation step specifically for
              granting <Text strong>superadmin</Text> itself — any existing superadmin can promote
              anyone else to superadmin through the same role dropdown used for every other role
              change, so treat that action with the same care you'd give any other irreversible
              privilege grant.
            </>
          }
        />
      </>
    ),
  },
];

interface HelpMenuNode {
  key: string;
  label: string;
  children?: HelpMenuNode[];
}

const HELP_MENU: HelpMenuNode[] = [
  { key: 'building-blocks', label: 'Building Blocks' },
  { key: 'infra-users', label: 'Checking Existing Infra Users' },
  {
    key: 'legacy-gen',
    label: 'Legacy: Generating InfraUser Accounts',
    children: [
      { key: 'bulk-excel', label: 'Bulk Excel' },
      { key: 'single-user', label: 'Single User' },
      { key: 'add-n-users', label: 'Add N Users' },
    ],
  },
  {
    key: 'workspace-sessions',
    label: 'New Workspace Sessions',
    children: [
      { key: 'batch-setup', label: 'Setting up New Batch' },
      { key: 'batch-monitoring', label: 'Monitoring Running Batches' },
      { key: 'workshop-sessions', label: 'Launching Workshop Sessions' },
    ],
  },
  { key: 'licensing', label: 'License Control Facility' },
  { key: 'user-extensions', label: 'Workflow for User Extension' },
  { key: 'lifecycle-cleanup', label: 'End-of-Life Cycle Control and Cleanup' },
  { key: 'user-admin', label: 'User Administration' },
  {
    key: 'implementation-insights',
    label: 'Implementation Insights',
    children: [
      { key: 'ic-iwm-overview', label: 'IC & IWM' },
      { key: 'deployment', label: 'Current Deployment Topology' },
    ],
  },
];

const HELP_TOPIC_KEYS = new Set(HELP_TOPICS.map((t) => t.key));

function buildMenuItems(nodes: HelpMenuNode[]): import('antd').MenuProps['items'] {
  return nodes.map((node) => {
    if (node.children) {
      return {
        key: node.key,
        label: node.label,
        children: buildMenuItems(node.children),
      };
    }
    return { key: node.key, label: node.label };
  });
}

export default function HelpPage() {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const [activeKey, setActiveKey] = useState<string>(HELP_TOPICS[0]?.key);

  const active = HELP_TOPICS.find((t) => t.key === activeKey) ?? HELP_TOPICS[0];

  return (
    <div className={`flex-1 p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
      {useNew ? (
        <PageHeader title="Help" purpose="How-to guides for InfraConsole features." />
      ) : (
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <QuestionCircleOutlined /> Help
          </h1>
          <p className="text-sm text-gray-500">How-to guides for InfraConsole features.</p>
        </div>
      )}

      <div className="flex gap-6">
        <Menu
          mode="inline"
          selectedKeys={[active?.key ?? '']}
          defaultOpenKeys={['legacy-gen', 'workspace-sessions', 'implementation-insights']}
          onClick={(e) => {
            if (HELP_TOPIC_KEYS.has(e.key)) {
              setActiveKey(e.key);
            }
          }}
          style={{ width: 260, flexShrink: 0 }}
          items={buildMenuItems(HELP_MENU)}
        />
        <div className="flex-1 max-w-4xl bg-white rounded-lg shadow-sm p-6">
          {active ? active.content : <Paragraph type="secondary">No help topics yet.</Paragraph>}
        </div>
      </div>
    </div>
  );
}
